import { HtmlComponent } from '@varo/graphql-schema-types';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { StyleProp, TextProps, TextStyle, View, ViewStyle } from 'react-native';
import {
  CustomRendererProps,
  CustomTextualRenderer,
  MixedStyleDeclaration,
  TNode,
  TPhrasing,
  TText,
} from 'react-native-render-html';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import { UnknownGQLType } from 'shared/legacySDUIAdapters/genericRouteAction';
import { deepEqual } from 'shared/utils/deepEqual';
import { getVXLColor } from 'shared/utils/getVXLColor/getVXLColor';
import { getVXLFont } from 'shared/utils/getVXLFont/getVXLFont';
import { Maybe } from 'shared/utils/types';

export interface HTMLComponentProps extends HtmlComponent {
  navigateForAction: (action: Maybe<UnknownGQLType>) => void;
}

const HTMLComponent = memo(
  (props: HTMLComponentProps) => {
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    const {
      html,
      textStyle,
      linkStyle,
      boldStyle,
      linkActions,
      navigateForAction,
    } = props;

    const body: TextProps | null = useMemo(() => {
      if (textStyle) {
        const styles = {
          ...getVXLFont(textStyle.fontStyle),
          color: getVXLColor(textStyle.color),
        };
        return {
          style: styles,
          p: {
            ...styles,
          },
        };
      }
      return null;
    }, [textStyle]);

    const bold: MixedStyleDeclaration | null = useMemo(() => {
      if (boldStyle) {
        return {
          ...getVXLFont(boldStyle.fontStyle),
          color: getVXLColor(boldStyle.color),
        } as MixedStyleDeclaration;
      }

      return null;
    }, [boldStyle]);

    const link: TextStyle | null = useMemo(() => {
      if (linkStyle) {
        const styles = {
          ...getVXLFont(linkStyle.fontStyle),
          color: getVXLColor(linkStyle.color),
          textDecorationColor: getVXLColor(linkStyle.color),
        };
        return {
          ...styles,
          a: {
            ...styles,
          },
        } as TextStyle;
      }

      return null;
    }, [linkStyle]);

    const onLinkPress: (href: string) => void = useCallback(
      (href: string) => {
        const action = linkActions?.find(
          (action) => action.id === href,
        ) as UnknownGQLType;
        navigateForAction(action);
      },
      [linkActions, navigateForAction],
    );

    const tagsStyles = useMemo(
      () => ({
        strong: bold ?? {},
        ...body,
        ...link,
      }),
      [body, bold, link],
    );

    const ATagRenderer: CustomTextualRenderer = useCallback(
      function <T extends TNode>({
        TDefaultRenderer,
        ...props
      }: CustomRendererProps<T>) {
        const { href } = props.tnode.attributes ?? {};
        return (
          <TDefaultRenderer
            {...props}
            onPress={() => onLinkPress(href)}
            style={
              [link ?? {}, props.style] as T extends TPhrasing | TText
                ? StyleProp<TextStyle>
                : StyleProp<ViewStyle>
            }
          />
        );
      },
      [onLinkPress, link],
    );

    const renderers = useMemo(
      () => ({
        a: ATagRenderer,
      }),
      [ATagRenderer],
    );

    return (
      <View
        onLayout={(event) => {
          setDimensions(event.nativeEvent.layout);
        }}
      >
        <HTMLRenderer
          htmlAsString={html}
          contentWidth={dimensions.width}
          tagsStyles={
            tagsStyles as unknown as Record<string, MixedStyleDeclaration>
          }
          renderers={renderers}
        />
      </View>
    );
  },
  (prevProps, nextProps) => {
    return deepEqual(prevProps, nextProps);
  },
);

export default HTMLComponent;
